"use client"
import { useState, useEffect, Suspense } from "react"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import Link from "next/link"
import Icon from "@/components/ui/Icon"
import { readStoredUser } from "@/lib/safe-storage"
import { safeNextPath } from "@/lib/safe-redirect"

import { ACCOUNT_TYPES, accountType, type AccountTypeId } from "@/lib/accounts/account-types"

export default function RegisterPage() {
  return <Suspense><RegisterPageInner /></Suspense>
}

function RegisterPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  // "signup" = fresh registration, "otp" = verify phone, "complete" = post-OTP profile finish
  const [sentTo, setSentTo] = useState("")
  const [step, setStep] = useState<"signup" | "otp" | "complete">("signup")
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "" })
  // The sign-up choice ("AGENCY" is an AGENT who also trades under an agency name).
  const [acctType, setAcctType] = useState<AccountTypeId>("BUYER")
  const [orgName, setOrgName] = useState("")
  const selectedType = accountType(acctType)!
  const role = selectedType.role
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [otp, setOtp] = useState("")
  const [devOtp, setDevOtp] = useState("")
  const [userId, setUserId] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  // Complete-mode only
  const [completePw, setCompletePw] = useState("")
  const [completeConfirm, setCompleteConfirm] = useState("")

  useEffect(() => {
    const stored = localStorage.getItem("ze_user")
    if (!stored) return
    const user = JSON.parse(stored)
    if (user.name) { router.push("/dashboard"); return }
    if (user.id) { setUserId(user.id); setStep("complete") }
  }, [])

  function setSession(user: any) {
    localStorage.setItem("ze_user", JSON.stringify(user))
    localStorage.setItem("ze_session_expiry", new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
    localStorage.setItem("ze_otp_last", String(Date.now()))
    window.dispatchEvent(new Event("ze:auth"))
  }

  function redirect(r: string) {
    const next = safeNextPath(searchParams.get("next"), "") || null
    if (r === "AGENT" || r === "OWNER" || r === "DEVELOPER") {
      router.push(next ? `/kyc?next=${encodeURIComponent(next)}` : "/kyc")
    } else {
      router.push(next ?? "/dashboard")
    }
  }

  // Step 1: collect details and send OTP
  async function handleSendOTP() {
    if (!form.name.trim()) { setError("Enter your full name"); return }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError("Enter a valid email address"); return }
    if (!form.password || form.password.length < 8) { setError("Password must be at least 8 characters"); return }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return }
    if (!form.phone) { setError("Enter your phone number including country code"); return }
    if (!termsAccepted) { setError("You must accept the Terms of Service and Privacy Policy to continue"); return }
    if (selectedType.orgName && !orgName.trim()) { setError(`Enter your ${selectedType.orgName.label.toLowerCase()}`); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: form.phone, email: form.email.trim() }) })
      const data = await res.json()
      if (!data.success) { setError(data.error ?? "Failed to send code"); setLoading(false); return }
      if (data.devOtp) setDevOtp(data.devOtp)
      setSentTo(data.channel === "email" ? (data.sentTo ?? form.email) : form.phone)
      setStep("otp")
    } catch { setError("Something went wrong") }
    setLoading(false)
  }

  // Step 2: verify OTP, then save profile and password
  async function handleVerifyOTP() {
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: form.phone, otp }) })
      const data = await res.json()
      if (!data.success) { setError(data.error ?? "Incorrect code"); setLoading(false); return }
      if (!data.isNew) { setError("This phone number is already registered. Log in instead."); setLoading(false); return }

      const uid = data.user.id
      await fetch("/api/auth/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, role, companyName: selectedType.orgName ? orgName.trim() : "", termsAcceptedAt: new Date().toISOString() }),
      })
      if (form.password) {
        const pwRes = await fetch("/api/auth/set-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: form.password, resetToken: data.resetToken }),
        })
        const pwData = await pwRes.json().catch(() => ({}))
        if (!pwData.success) console.error("[register] set-password failed:", pwData.error)
      }

      setSession({ id: uid, phone: data.user.phone, name: form.name, email: form.email, role })
      redirect(role)
    } catch { setError("Something went wrong") }
    setLoading(false)
  }

  // Complete mode: for users who verified phone via login page OTP fallback
  async function handleComplete() {
    if (!form.name.trim()) { setError("Enter your full name"); return }
    if (selectedType.orgName && !orgName.trim()) { setError(`Enter your ${selectedType.orgName.label.toLowerCase()}`); return }
    if (completePw) {
      if (completePw.length < 8) { setError("Password must be at least 8 characters"); return }
      if (completePw !== completeConfirm) { setError("Passwords do not match"); return }
    }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, role, companyName: selectedType.orgName ? orgName.trim() : "" }),
      })
      const data = await res.json()
      if (!data.success) { setError(data.error ?? "Failed to save"); setLoading(false); return }
      if (completePw) {
        await fetch("/api/auth/set-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: completePw }),
        })
      }
      const stored = readStoredUser()
      localStorage.setItem("ze_user", JSON.stringify({ ...stored, name: form.name, email: form.email, role }))
      redirect(role)
    } catch { setError("Something went wrong") }
    setLoading(false)
  }

  const inputStyle: React.CSSProperties = { width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none", boxSizing: "border-box" }

  // From useSearchParams, not window.location: the server has no window, so the
  // href differed from the server HTML and React 19 keeps the server's attribute,
  // which silently dropped ?next= from the Google button.
  const nextParam = searchParams.get("next") ?? ""
  const oauthNext = nextParam ? "?next=" + encodeURIComponent(nextParam) : ""

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Link href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 32, fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", textDecoration: "none" }}>
            <Image src="/icon-full-color.svg" width={36} height={36} alt="" style={{ background: "var(--cream-warm)", borderRadius: 8, padding: 4, flexShrink: 0, display: "block" }} />
            Zim <span style={{ color: "var(--gold)" }}>Estates</span>
          </Link>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            {step === "otp" ? "Verify your number" : step === "complete" ? "Complete your profile" : "Create your account"}
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 15 }}>
            {step === "otp" ? `We sent a code to ${sentTo || form.phone}. Enter it below.` : step === "complete" ? "Just a few details to finish setting up." : "Free account. Verified listings. Direct contact with agents."}
          </p>
        </div>

        {step !== "otp" && (
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 24 }}>
            {(step === "signup" ? ["details", "verify"] : ["details", "verify"]).map((s, i) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: i === 0 ? "var(--navy)" : "var(--cream-warm)", border: "1px solid var(--line)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, color: i === 0 ? "var(--cream)" : "var(--muted)" }}>{i + 1}</div>
                <span style={{ fontSize: 12, color: i === 0 ? "var(--navy)" : "var(--muted)", fontWeight: i === 0 ? 600 : 400 }}>{s === "details" ? "Your details" : "Verify phone"}</span>
                {i === 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>›</span>}
              </div>
            ))}
          </div>
        )}

        <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 20, padding: 32, boxShadow: "var(--shadow-lg)" }}>

          {/* OAuth options, only on initial signup step */}
          {step === "signup" && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                <a href={"/api/auth/oauth/google" + oauthNext}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", padding: "12px 16px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "var(--navy)", background: "var(--cream)", textDecoration: "none", fontFamily: "inherit" }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Continue with Google
                </a>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500 }}>or sign up with email</span>
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
              </div>
            </>
          )}

          {/* Step 1: signup details */}
          {step === "signup" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="mob-grid-2" style={{ gap: 12 }}>
                <div>
                  <label htmlFor="reg-name" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Full name</label>
                  <input id="reg-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Tendai Moyo" autoComplete="name" style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="reg-email" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Email address</label>
                  <input id="reg-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="tendai@example.com" type="email" autoComplete="email" style={inputStyle} />
                </div>
              </div>

              <div className="mob-grid-2" style={{ gap: 12 }}>
                <div>
                  <label htmlFor="reg-password" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Password</label>
                  <input id="reg-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} type="password" placeholder="At least 8 characters" autoComplete="new-password" style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="reg-confirm" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Confirm password</label>
                  <input id="reg-confirm" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} type="password" placeholder="Repeat password" autoComplete="new-password" style={inputStyle} />
                </div>
              </div>

              <div>
                <label htmlFor="reg-phone" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Phone number</label>
                <input id="reg-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+263 77 234 5678" type="tel" autoComplete="tel" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Include country code (e.g. +263 for Zimbabwe, +44 for UK). We will send a verification code.</div>
                <div style={{ fontSize: 12, color: "var(--gold-deep)", marginTop: 4 }}>Diaspora? Use your UK/SA/US number, or sign up faster with Google above.</div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>I am a</label>
                <div className="mob-grid-2" style={{ gap: 10 }}>
                  {ACCOUNT_TYPES.map(t => (
                    <div key={t.id} onClick={() => setAcctType(t.id)} role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAcctType(t.id) } }}
                      style={{ border: "2px solid " + (acctType === t.id ? "var(--navy)" : "var(--line)"), borderRadius: 12, padding: 14, cursor: "pointer", background: acctType === t.id ? "rgba(27,45,84,0.04)" : "var(--cream)", transition: "all 0.15s" }}>
                      <div style={{ marginBottom: 6 }}><Icon name={t.icon} size={20} /></div>
                      <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13, marginBottom: 2 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.desc}</div>
                    </div>
                  ))}
                </div>
                {/* An agency or development company trades under its own name, and that
                    is what buyers see on its listings instead of the owner's name. */}
                {selectedType.orgName && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{selectedType.orgName.label}</label>
                    <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={selectedType.orgName.placeholder}
                      style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none", boxSizing: "border-box" }} />
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Buyers will see this name on your listings, not your personal name.</div>
                  </div>
                )}
              </div>

              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8 }}>{error}</div>}

              <button onClick={handleSendOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Sending code..." : "Send verification code"}
              </button>
              <div style={{ textAlign: "center", fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>We will email your verification code to the address above. Check your spam folder if it does not arrive within a minute.</div>

              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, accentColor: "var(--navy)", cursor: "pointer" }}
                />
                <span style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                  I have read and agree to the{" "}
                  <Link href="/terms" target="_blank" style={{ color: "var(--navy)", fontWeight: 600 }}>Terms of Service</Link>
                  {" "}and{" "}
                  <Link href="/privacy" target="_blank" style={{ color: "var(--navy)", fontWeight: 600 }}>Privacy Policy</Link>.
                  {" "}<span style={{ color: "var(--red-text)" }}>*</span>
                </span>
              </label>
            </div>
          )}

          {/* Step 2: OTP verification */}
          {step === "otp" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {devOtp && (
                <div style={{ background: "rgba(200,150,62,0.1)", border: "1px solid rgba(200,150,62,0.3)", borderRadius: 10, padding: 12, fontSize: 13, color: "var(--gold-deep)", textAlign: "center" }}>
                  Sandbox code: <strong>{devOtp}</strong>
                </div>
              )}
              <div>
                <label htmlFor="reg-otp" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>6-digit code</label>
                <input id="reg-otp" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").substring(0, 6))} onKeyDown={e => e.key === "Enter" && handleVerifyOTP()} placeholder="000000" maxLength={6} type="text" inputMode="numeric" autoComplete="one-time-code" style={{ width: "100%", padding: "16px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 32, fontFamily: "Fraunces, serif", fontWeight: 600, textAlign: "center", letterSpacing: 12, background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8 }}>{error}</div>}
              <button onClick={handleVerifyOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Verifying..." : "Verify and create account"}
              </button>
              <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                Wrong number?{" "}
                <button onClick={() => { setStep("signup"); setOtp(""); setError(""); setDevOtp("") }} style={{ color: "var(--gold-deep)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Go back</button>
                {" · "}
                <button onClick={handleSendOTP} disabled={loading} style={{ color: "var(--muted)", fontWeight: 600, background: "none", border: "none", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "inherit" }}>Resend code</button>
              </div>
            </div>
          )}

          {/* Complete mode: for OTP-only users who need to finish profile */}
          {step === "complete" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label htmlFor="complete-name" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Full name</label>
                <input id="complete-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Tendai Moyo" autoComplete="name" style={inputStyle} />
              </div>
              <div>
                <label htmlFor="complete-email" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Email address (optional)</label>
                <input id="complete-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="tendai@example.com" type="email" autoComplete="email" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>I am a</label>
                <div className="mob-grid-2" style={{ gap: 10 }}>
                  {ACCOUNT_TYPES.map(t => (
                    <div key={t.id} onClick={() => setAcctType(t.id)} role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAcctType(t.id) } }}
                      style={{ border: "2px solid " + (acctType === t.id ? "var(--navy)" : "var(--line)"), borderRadius: 12, padding: 14, cursor: "pointer", background: acctType === t.id ? "rgba(27,45,84,0.04)" : "var(--cream)", transition: "all 0.15s" }}>
                      <div style={{ marginBottom: 6 }}><Icon name={t.icon} size={20} /></div>
                      <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13, marginBottom: 2 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.desc}</div>
                    </div>
                  ))}
                </div>
                {/* An agency or development company trades under its own name, and that
                    is what buyers see on its listings instead of the owner's name. */}
                {selectedType.orgName && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{selectedType.orgName.label}</label>
                    <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={selectedType.orgName.placeholder}
                      style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none", boxSizing: "border-box" }} />
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Buyers will see this name on your listings, not your personal name.</div>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="complete-password" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Set a password (optional)</label>
                <input id="complete-password" value={completePw} onChange={e => setCompletePw(e.target.value)} type="password" placeholder="At least 8 characters" autoComplete="new-password" style={{ ...inputStyle, marginBottom: 8 }} />
                {completePw && <input id="complete-confirm" value={completeConfirm} onChange={e => setCompleteConfirm(e.target.value)} type="password" placeholder="Confirm password" autoComplete="new-password" style={inputStyle} />}
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Lets you sign in with email and password in future.</div>
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8 }}>{error}</div>}
              <button onClick={handleComplete} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Saving..." : "Save and continue"}
              </button>
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--muted)" }}>
          Already have an account?{" "}
          <Link href="/auth/login" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Log in</Link>
        </div>
      </div>
    </div>
  )
}
